import { Component, OnInit, DestroyRef, inject } from "@angular/core";
import { FormGroupDirective } from "@angular/forms";
import { FormText, FormGroup } from "@coso/forms";
import { ISettings, SubfolderNameSource, SubfolderNameSourceName } from "../../../../models/auto-generated";
import { Constants } from "../../constants";
import { BehaviorSubject } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
  standalone:false,
  templateUrl: "./general-settings.component.html",
  styleUrls: ["./general-settings.component.scss"]
})
export class GeneralSettingsComponent implements OnInit {
  formGroup: FormGroup<ISettings>;
  formText: FormText<ISettings>;
  
  isReadOnly = new BehaviorSubject<boolean>(false);
  private destroyRef = inject(DestroyRef);

  get isReadOnly$() { 
    return this.isReadOnly.asObservable();
  }

  constructor(private rootFormGroupDirective: FormGroupDirective) { }

  ngOnInit(): void {    

    this.formGroup = this.rootFormGroupDirective.control.get("settings") as FormGroup<ISettings>;
    this.formText = {
      documentLocationName: {
        label: "Location Name (at Dynamics 365)",
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-5",
        popover: "The name of the document location in Dynamics 365. The following placeholders can be used: {0} for the name of the object and {1} for the id of the object.",
        placeholder: Constants.DefaultDocumentLocationPattern,
        errors: {
          required: "The Location Name (at Dynamics 365) field is required."
        }
      },
      suppressLeadBehavior: {
        disable: "Off",
        enable: "On",
        label: "Suppress Lead Behavior",
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-5",
        popover: "Skip default behavior for lead entity when on Lead qualification the document location for the newly created account, contact, and opportunity is pointing to the lead folder."
      },
      suppressOpportunityBehavior: {
        disable: "Off",
        enable: "On",
        label: "Suppress Opportunity Behavior",
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-5",
        popover: "Skip default behavior for opportunity entity in which the document location for quote, sales order, and invoice is pointing to the opportunity folder."
      },
      supportOneNote: {
        disable: "Off",
        enable: "On",
        label: "Support OneNote Integration",
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-5",
        popover: "Handling for OneNote Integration, when OneNote document is moved to SharePoint Structure Creator document location and original location with folder is deleted (if empty)."
      },
      breakRoleInheritance: {
        disable: "Off",
        enable: "On",
        label: "Break Role Inheritance",
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-5",
        popover: "Specifies whether newly created securable object (folder) referenced by created document location should have broken permission inheritance (unique permissions) and should include permission for calling user."
      },
      copyRoleAssignments: {
        disable: "Off",
        enable: "On",
        label: "Copy Role Assignments",
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-5",
        popover: "Specifies whether to copy the role assignments from the parent securable object when breaking permission inheritance."
      },
      skipSubfolder: {
        disable: "Off",
        enable: "On",
        label: "Structure based on: Skip Subfolder Creation",
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-5",
        popover: "Specifies whether subfolder with entity name (or custom value) will be created when 'Structure based on' account or contact is configured. If subfolder would be skipped, the entities will be placed directly below parent location, e.g.: '/account/Connecting Software/CAS-012345' instead of '/account/Connecting Software/incident/CAS-012345'"
      },
      subfolderNameSource: {
        label: "Structure based on: Folder Name Source",
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-5",
        popover: "Determines the naming source for subfolders when 'Structure based on' account or contact is configured. Select 'Logical Name' to use the entity's logical name, or 'Display Name' to use the language-specific display name.",
        options: Object.keys(SubfolderNameSource)
          .filter((k) => isNaN(Number(k)))
          .map((k) => ({ label: SubfolderNameSourceName.get(SubfolderNameSource[k]), value: SubfolderNameSource[k] }))
      },
    };

    this.isReadOnly$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isReadOnly) => {

        if (this.formGroup.controls.breakRoleInheritance.value  && !isReadOnly) {
          this.formGroup.controls.copyRoleAssignments.enable();
        } else {
          this.formGroup.controls.copyRoleAssignments.disable();      
        }
        
        if (!this.formGroup.controls.skipSubfolder.value  && !isReadOnly) {
          this.formGroup.controls.subfolderNameSource.enable();
        } else {
          this.formGroup.controls.subfolderNameSource.disable();      
        }        
      });
  }
}
